/*
  RecordingProvider — a top-level context that owns the MediaRecorder
  and MediaStream so an ongoing SOAP assessment recording survives
  route changes.

  Design
  ------
  - Only one active recording at a time (a therapist should not run two
    physical assessments in parallel from the same browser).
  - Audio is captured as short (~8 min) self-contained webm segments so
    each one fits within Whisper's 25MB limit and can be transcribed
    independently, even if the browser crashes mid-session.
  - Segments are queued and uploaded FIFO. If an upload fails the
    segment is retried a few times, then flagged.
  - beforeunload guard prevents accidental tab close while recording.
*/
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { getSavedMicDeviceId, saveMicDeviceId } from "@/components/MicPicker";

const RecordingCtx = createContext(null);

const SEGMENT_MS = 8 * 60 * 1000;   // 8 minute segments — ~3MB webm/opus
const MAX_TOTAL_MS = 2 * 60 * 60 * 1000; // 2 hour hard cap

function pickMimeType() {
  const options = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
  ];
  for (const t of options) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return "audio/webm";
}

export function RecordingProvider({ children }) {
  // Refs — hold objects we don't want to trigger re-renders
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);         // in-flight chunks for current segment
  const segmentIndexRef = useRef(0);
  const rotateTimerRef = useRef(null);
  const cappedRef = useRef(false);
  const startedAtRef = useRef(0);
  const uploadingRef = useRef(false);
  const uploadQueueRef = useRef([]);    // [{index, blob}]

  // State — anything the UI needs to render
  const [session, setSession] = useState(null); // {id, client_email, client_name}
  const [status, setStatus] = useState("idle"); // idle | starting | recording | paused | stopping | finalizing | done | error
  const [elapsedMs, setElapsedMs] = useState(0);
  const [pendingUploads, setPendingUploads] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [segmentsRecorded, setSegmentsRecorded] = useState(0);
  const [failedSegments, setFailedSegments] = useState(0);
  const [lastUploadAt, setLastUploadAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Elapsed timer — driven off startedAtRef so pausing works cleanly
  useEffect(() => {
    if (status !== "recording") return;
    const id = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 500);
    return () => clearInterval(id);
  }, [status]);

  // beforeunload guard while a recording is active
  useEffect(() => {
    if (status !== "recording" && status !== "starting" && status !== "finalizing") return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status]);

  // ------------- Upload queue -------------
  const drainQueue = useCallback(async () => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    try {
      while (uploadQueueRef.current.length > 0) {
        const job = uploadQueueRef.current[0];
        try {
          const fd = new FormData();
          fd.append("segment_index", String(job.index));
          fd.append("file", job.blob, `${String(job.index).padStart(4, "0")}.webm`);
          await api.post(`/soap-notes/sessions/${job.sessionId}/segments`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            transformRequest: (data, headers) => {
              // Let the browser set the boundary automatically
              delete headers["Content-Type"];
              return data;
            },
          });
          uploadQueueRef.current.shift();
          setUploadedCount((n) => n + 1);
          setPendingUploads(uploadQueueRef.current.length);
          setLastUploadAt(Date.now());
        } catch (e) {
          // Backoff and retry — but only twice; then abandon this segment
          // so the rest of the session can still finalise.
          job.retries = (job.retries || 0) + 1;
          if (job.retries > 2) {
            uploadQueueRef.current.shift();
            setPendingUploads(uploadQueueRef.current.length);
            setFailedSegments((n) => n + 1);
            toast.error("Audio segment failed to upload", {
              description: `Segment #${job.index} dropped after 3 attempts. ${e?.response?.data?.detail || e.message}`,
            });
          } else {
            await new Promise((r) => setTimeout(r, 1500 * job.retries));
          }
        }
      }
    } finally {
      uploadingRef.current = false;
    }
  }, []);

  const enqueueSegment = useCallback((sessionId, index, blob) => {
    uploadQueueRef.current.push({ sessionId, index, blob, retries: 0 });
    setPendingUploads(uploadQueueRef.current.length);
    drainQueue();
  }, [drainQueue]);

  // ------------- Recorder lifecycle -------------
  const closeStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null;
  };

  const startNewSegmentRecorder = useCallback((sessionId) => {
    if (!streamRef.current) return;
    const mimeType = pickMimeType();
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, {
        mimeType,
        audioBitsPerSecond: 48000,
      });
    } catch {
      // Some browsers don't accept audioBitsPerSecond — fall back
      rec = new MediaRecorder(streamRef.current, { mimeType });
    }
    chunksRef.current = [];
    const thisIndex = segmentIndexRef.current;
    rec.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      if (blob.size > 0) enqueueSegment(sessionId, thisIndex, blob);
    };
    rec.start();
    recorderRef.current = rec;
    segmentIndexRef.current += 1;
    setSegmentsRecorded(segmentIndexRef.current);

    // Rotate after SEGMENT_MS — stop this recorder, then immediately
    // start the next one on the same stream.
    rotateTimerRef.current = setTimeout(() => {
      const elapsed = Date.now() - startedAtRef.current;
      // Hard cap: 2 hours — auto-stop.
      if (elapsed >= MAX_TOTAL_MS) {
        cappedRef.current = true;
        // Signal outer stop path below by calling stopRecording via ref
        try { rec.stop(); } catch { /* ignore */ }
        toast.warning("Recording auto-stopped", {
          description: "2-hour maximum reached. Finalise to generate SOAP note.",
        });
        setStatus("recording"); // let stopRecording handle the transition
        // Trigger stop next tick so onstop fires first
        setTimeout(() => stopRecordingInternal(true), 50);
        return;
      }
      try { rec.stop(); } catch { /* ignore */ }
      startNewSegmentRecorder(sessionId);
    }, SEGMENT_MS);
  }, [enqueueSegment]);

  // stopRecordingInternal is used both by user-stop AND the 2-hour cap.
  const stopRecordingInternal = useCallback((wasCapped = false) => {
    if (rotateTimerRef.current) {
      clearTimeout(rotateTimerRef.current);
      rotateTimerRef.current = null;
    }
    setStatus("stopping");
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch { /* ignore */ }
    // Give onstop a moment to fire and enqueue the last chunk
    setTimeout(() => {
      closeStream();
      // We don't reset session yet — the UI will call finalize() next.
      setStatus("stopped");
    }, 150);
    if (wasCapped) cappedRef.current = true;
  }, []);

  const startRecording = useCallback(async ({ client_email, client_name, appointment_type, sbn_client_id, notes, template_id, follow_up, initiating_assessment_id, canonical_client_id }) => {
    if (status === "recording" || status === "starting") {
      throw new Error("A recording is already in progress");
    }
    // If a previous recording is sitting in "stopped" (or finalizing/done/
    // error) waiting to be processed, silently PARK it — audio segments
    // stay on disk, backend session row survives, and the therapist can
    // process it later from the client's Notes section. This is what lets
    // them go straight from one consultation to the next.
    if (session && status !== "idle") {
      segmentIndexRef.current = 0;
      cappedRef.current = false;
      // Stop any live recorder just in case (e.g. status stuck in "stopping")
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch { /* ignore */ }
      if (rotateTimerRef.current) {
        clearTimeout(rotateTimerRef.current);
        rotateTimerRef.current = null;
      }
      closeStream();
      // Drop the local session — upload queue keeps draining independently.
      setSession(null);
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not support audio recording.");
    }
    setErrorMsg("");
    setStatus("starting");
    setUploadedCount(0);
    setPendingUploads(0);
    setSegmentsRecorded(0);
    setFailedSegments(0);
    setLastUploadAt(null);
    segmentIndexRef.current = 0;
    cappedRef.current = false;
    // 1. Create the session on the backend so it exists before we
    //    start uploading segments.
    let sessionRes;
    try {
      sessionRes = await api.post("/soap-notes/sessions", {
        client_email, client_name, appointment_type, sbn_client_id, notes, template_id,
        follow_up: follow_up || null,
        initiating_assessment_id: initiating_assessment_id || null,
        canonical_client_id: canonical_client_id || null,
      });
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.response?.data?.detail || e.message || "Failed to create session");
      throw e;
    }
    const sess = sessionRes.data.session;
    // 2. Ask for mic access — prefer the therapist's explicitly saved
    //    device (see MicPicker). If that device is gone (unplugged),
    //    fall back to the system default and clear the saved id so the
    //    picker can prompt for a new choice.
    let stream;
    const savedDeviceId = getSavedMicDeviceId();
    const baseAudio = { echoCancellation: true, noiseSuppression: true, channelCount: 1 };
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: savedDeviceId
          ? { ...baseAudio, deviceId: { exact: savedDeviceId } }
          : baseAudio,
      });
    } catch (e) {
      // OverconstrainedError / NotFoundError → device is gone. Try
      // again with system default, and forget the saved id.
      const retriable = e?.name === "OverconstrainedError"
        || e?.name === "NotFoundError"
        || (savedDeviceId && (e?.name === "NotReadableError" || e?.name === "AbortError"));
      if (retriable) {
        saveMicDeviceId("");
        toast.warning("Selected microphone unavailable", {
          description: "Falling back to the system default. Update your choice in the mic picker.",
        });
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: baseAudio });
        } catch (e2) {
          try { await api.delete(`/soap-notes/sessions/${sess.id}`); } catch { /* ignore */ }
          setStatus("error");
          setErrorMsg("Microphone access denied. Please allow it in your browser.");
          throw e2;
        }
      } else {
        try { await api.delete(`/soap-notes/sessions/${sess.id}`); } catch { /* ignore */ }
        setStatus("error");
        setErrorMsg("Microphone access denied. Please allow it in your browser.");
        throw e;
      }
    }
    streamRef.current = stream;
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    setSession({
      id: sess.id, client_email: sess.client_email, client_name: sess.client_name,
      initiating_assessment_id: sess.initiating_assessment_id || null,
    });
    setStatus("recording");
    startNewSegmentRecorder(sess.id);
  }, [status, startNewSegmentRecorder]);

  const stopRecording = useCallback(() => {
    stopRecordingInternal(false);
  }, [stopRecordingInternal]);

  // Finalise — waits for uploads to drain, then triggers backend
  // transcription + LLM. The backend now runs the job in a background
  // task and returns immediately with status="finalizing"; we poll
  // GET /sessions/{id} until status flips to done or failed. This
  // survives Cloudflare's 100s proxy timeout for long recordings.
  const finalize = useCallback(async ({ extra_context, include_sbn = true, template_id, follow_up } = {}) => {
    if (!session?.id) throw new Error("No active session to finalise");
    setStatus("finalizing");
    // Wait for the upload queue to drain (max 60s to be defensive)
    const waitStart = Date.now();
    while (uploadQueueRef.current.length > 0 && Date.now() - waitStart < 60000) {
      await new Promise((r) => setTimeout(r, 500));
    }
    // Kick off the job. This returns fast; we then poll for the note.
    try {
      await api.post(`/soap-notes/sessions/${session.id}/finalize`, {
        extra_context: extra_context || "",
        include_sbn,
        template_id: template_id || null,
        follow_up: follow_up || null,
      });
    } catch (e) {
      // If server is unreachable, fall through to polling — the server
      // may have accepted the request and still be running.
      if (e?.response && e.response.status !== 200) {
        setStatus("error");
        setErrorMsg(e?.response?.data?.detail || e.message);
        throw e;
      }
    }
    try {
      localStorage.setItem("soap_pending_session_id", session.id);
    } catch { /* ignore */ }
    try {
      const res = await pollUntilDone(session.id);
      try { localStorage.removeItem("soap_pending_session_id"); } catch { /* ignore */ }
      setStatus("done");
      return res;
    } catch (e) {
      setStatus("error");
      setErrorMsg(e.message);
      throw e;
    }
  }, [session]);

  // Poll GET /sessions/{id} until status is done or failed. Does NOT
  // mutate global recording state — callers decide what to do with the
  // returned {session, note}. Returns on done, throws on failed.
  const pollUntilDone = useCallback(async (sessionId, opts = {}) => {
    const startedAt = Date.now();
    const maxMs = opts.maxMs ?? 15 * 60 * 1000;
    let intervalMs = 2000;
    while (true) {
      if (Date.now() - startedAt > maxMs) {
        throw new Error("SOAP generation is taking longer than 15 minutes. Use the recovery banner to retry.");
      }
      let data;
      try {
        const r = await api.get(`/soap-notes/sessions/${sessionId}`);
        data = r.data;
      } catch (e) {
        // Network blip — keep trying with backoff.
        await new Promise((r) => setTimeout(r, intervalMs));
        intervalMs = Math.min(intervalMs * 1.5, 10000);
        continue;
      }
      const st = data?.session?.status;
      if (st === "done" && data.note) {
        return { session: data.session, note: data.note };
      }
      if (st === "failed") {
        throw new Error(data?.session?.error || "SOAP generation failed");
      }
      // Still finalizing — keep polling
      await new Promise((r) => setTimeout(r, intervalMs));
      intervalMs = Math.min(intervalMs + 500, 5000);
    }
  }, []);

  // Recordings started from an assessment process automatically on Stop.
  // The open recorder dialog registers its latest notes/template here.
  const autoFinaliseOptsRef = useRef({});
  const autoFinalisedRef = useRef(new Set());
  const setAutoFinaliseOptions = useCallback((opts) => { autoFinaliseOptsRef.current = opts || {}; }, []);
  useEffect(() => {
    const assessmentId = session?.initiating_assessment_id;
    if (status !== "stopped" || !assessmentId || autoFinalisedRef.current.has(session.id)) return;
    autoFinalisedRef.current.add(session.id);
    const { assessmentId: optsFor, ...opts } = autoFinaliseOptsRef.current;
    finalize(optsFor === assessmentId ? opts : {}).catch((e) => {
      toast.error("Couldn't process the recording", {
        description: e?.response?.data?.detail || e.message,
        position: "top-center",
      });
    });
  }, [status, session, finalize]);

  // Retry a session that failed or was interrupted. Standalone — doesn't
  // require a live recording. Used by the recovery banner.
  const retryFinalize = useCallback(async (sessionId, { extra_context = "", include_sbn = true, template_id = null } = {}) => {
    try {
      await api.post(`/soap-notes/sessions/${sessionId}/retry`, {
        extra_context, include_sbn, template_id,
      });
    } catch (e) {
      if (e?.response?.status === 410) {
        // no audio on disk — nothing we can do
        throw new Error(e?.response?.data?.detail || "Audio no longer available");
      }
      // Any other kickoff error: still fall through to polling — the
      // server may already be running.
    }
    return await pollUntilDone(sessionId);
  }, [pollUntilDone]);

  const resetSession = useCallback(() => {
    // Called after the therapist reviews/saves the generated note.
    setSession(null);
    setStatus("idle");
    setElapsedMs(0);
    setUploadedCount(0);
    setPendingUploads(0);
    setSegmentsRecorded(0);
    setFailedSegments(0);
    setLastUploadAt(null);
    setErrorMsg("");
  }, []);

  // Park a stopped recording so the therapist can start a new
  // consultation for the NEXT client immediately, without finalising
  // the previous one. The backend session row + audio segments stay
  // intact — the therapist can process notes later from the client's
  // Notes section (via SoapNotesList → PendingConsultationsForClient).
  const parkSession = useCallback(() => {
    if (rotateTimerRef.current) {
      clearTimeout(rotateTimerRef.current);
      rotateTimerRef.current = null;
    }
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch { /* ignore */ }
    closeStream();
    // Reset local state but DO NOT delete the backend session — the
    // audio-on-disk + soap_sessions row is exactly what the Notes-
    // section banner will use to offer "Process notes now" later.
    resetSession();
  }, [resetSession]);

  const abandonSession = useCallback(async () => {
    // Called when the therapist bails out mid-recording without finalising.
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    if (rotateTimerRef.current) clearTimeout(rotateTimerRef.current);
    closeStream();
    if (session?.id) {
      // Force=true because the user explicitly chose to discard — this
      // is different from a browser crash where we WANT the audio kept.
      try { await api.delete(`/soap-notes/sessions/${session.id}?force=true`); } catch { /* ignore */ }
    }
    try { localStorage.removeItem("soap_pending_session_id"); } catch { /* ignore */ }
    resetSession();
  }, [session, resetSession]);

  // Discard a pending/failed session from the recovery banner without
  // touching the currently-active recording state.
  const discardPendingSession = useCallback(async (sessionId) => {
    try {
      await api.delete(`/soap-notes/sessions/${sessionId}?force=true`);
    } catch (e) {
      throw e;
    }
  }, []);

  // Fetch the recovery list (sessions with audio on disk but no note yet).
  const listPendingSessions = useCallback(async () => {
    try {
      const r = await api.get("/soap-notes/sessions/pending");
      return r.data?.sessions || [];
    } catch (e) {
      return [];
    }
  }, []);

  const value = useMemo(() => ({
    session,
    status,
    elapsedMs,
    pendingUploads,
    uploadedCount,
    segmentsRecorded,
    failedSegments,
    lastUploadAt,
    errorMsg,
    startRecording,
    stopRecording,
    finalize,
    retryFinalize,
    pollUntilDone,
    listPendingSessions,
    discardPendingSession,
    resetSession,
    abandonSession,
    parkSession,
    setAutoFinaliseOptions,
    isActive: status !== "idle" && status !== "done" && status !== "error",
  }), [
    session, status, elapsedMs, pendingUploads, uploadedCount,
    segmentsRecorded, failedSegments, lastUploadAt, errorMsg,
    startRecording, stopRecording, finalize, retryFinalize,
    pollUntilDone, listPendingSessions, discardPendingSession,
    resetSession, abandonSession, parkSession, setAutoFinaliseOptions,
  ]);

  return <RecordingCtx.Provider value={value}>{children}</RecordingCtx.Provider>;
}

export function useRecording() {
  const ctx = useContext(RecordingCtx);
  if (!ctx) throw new Error("useRecording must be used inside <RecordingProvider>");
  return ctx;
}
